import { useEffect, useState } from 'react';
import usePush from './usePush';
import AccountService from '../services/account-service';
import useShortCode from './useShortCode';

const permissionsCache = new Map();

/**
 * Hook to fetch and return user permissions that automatically refetches when shortCode changes
 * Includes caching per organization to avoid unnecessary API calls
 * @returns {Object} User permissions object with loading state
 */
function useUserPermissions() {
  const shortCode = useShortCode();
  const [user, setUser] = useState(null);

  const { execute, loading, error } = usePush(AccountService.getCurrentUser, {
    suspense: false,
  });

  useEffect(() => {
    if (shortCode) {
      const cacheKey = `permissions_${shortCode}`;
      const cachedUser = permissionsCache.get(cacheKey);

      if (cachedUser) {
        setUser(cachedUser);
        return;
      }
      const fetchUserPermissions = async () => {
        const [err, res] = await execute();

        if (!err && res) {
          setUser(res);
          permissionsCache.set(cacheKey, res);
        }
      };

      fetchUserPermissions();
    }
  }, [shortCode, execute]);

  const refetch = async () => {
    const [err, res] = await execute();
    if (!err && res) {
      setUser(res);
      if (shortCode) {
        const cacheKey = `permissions_${shortCode}`;
        permissionsCache.set(cacheKey, res);
      }
    }
    return res;
  };

  const clearCache = () => {
    permissionsCache.clear();
  };

  const clearCacheForOrg = orgShortCode => {
    const cacheKey = `permissions_${orgShortCode}`;
    permissionsCache.delete(cacheKey);
  };

  return {
    permissions: user?.permissions,
    loading,
    error,
    refetch,
    clearCache,
    clearCacheForOrg,
  };
}

export default useUserPermissions;
